import React, { useEffect, useState } from "react";
import styles from "./RecordVendorPaymentModal.module.css";
import { toast } from "react-toastify";
import CloseButton from "../Customs/Button/CrossButton";
import { recordVendorPayment } from "../Services/ledger";

interface RecordVendorPaymentModalProps {
  open: boolean;
  vendorId: number;
  vendorName?: string;
  outstanding?: number | string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "UPI", label: "Online UPI" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
];

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "" || value === "null") {
    return "--";
  }
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const RecordVendorPaymentModal: React.FC<RecordVendorPaymentModalProps> = ({
  open,
  vendorId,
  vendorName,
  outstanding,
  onClose,
  onSuccess,
}) => {
  const [paymentDate, setPaymentDate] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0].value);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPaymentDate("");
      setAmount("");
      setPaymentMethod(PAYMENT_METHODS[0].value);
      setReference("");
      setNotes("");
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentDate) {
      setError("Payment date is required");
      toast.error("Payment date is required");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError("Amount must be greater than zero");
      toast.error("Amount must be greater than zero");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await recordVendorPayment(vendorId, {
        amount: Number(amount),
        payment_date: `${paymentDate}T00:00:00Z`,
        payment_method: paymentMethod,
        reference_number: reference,
        notes,
      });
      toast.success("Vendor payment recorded");
      if (onSuccess) onSuccess();
      onClose();
    } catch {
      setError("Could not record payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Record Vendor Payment</h2>
          <CloseButton onClick={onClose} className={styles.closeBtn} />
        </div>
        {error && <div className={styles.apiError}>{error}</div>}
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.summaryBox}>
            <div>
              <b>Vendor:</b> <span>{vendorName || vendorId}</span>
            </div>
            <div>
              <b>Outstanding payable:</b> <span>{formatMoney(outstanding)}</span>
            </div>
          </div>
          <div className={styles.formRow2Col}>
            <div>
              <label>Payment Date</label>
              <input
                type="date"
                className={styles.input}
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div>
              <label>Amount Paid</label>
              <input
                type="number"
                className={styles.input}
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <div className={styles.formRow2Col}>
            <div>
              <label>Payment Method</label>
              <select
                className={styles.input}
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option value={m.value} key={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Reference Number (Optional)</label>
              <input
                className={styles.input}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Transaction ID, Check No., etc."
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <label>Notes (Optional)</label>
            <textarea
              className={styles.input}
              rows={3}
              placeholder="Add any additional notes about this payment..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn} disabled={loading}>
              {loading ? "Recording..." : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordVendorPaymentModal;
